import { defaultBreakpoints } from 'style-utils/responsive-utils/media/media';
import { InternalConfigObject } from '../../core.types';
import { merge } from './merge';
import { parseResponsiveObject } from './parseResponsiveObject';
import { sort } from './sort';
import { getNestedPropertyOrDefault } from 'style-utils/core/createExtractCssFromProps/utils/getNestedPropertyOrDefault';

//this function is used to create the parser function which is used to map the props to the appropriate css
export const createParser = (config: InternalConfigObject) => {
  //we create a cache object to store the breakpoints and the media queries
  const cache: Record<string, any> = {};
  //this is the actual parser function
  const parse = (props: Record<string, any>) => {
    //we create an empty object that will contain the css object which will be injected into the styled component
    let styles = {};
    //we create a flag to check if we need to sort the object-based responsive styles
    let shouldSort = false;
    //we check if the cache is disabled from the theme
    const isCacheDisabled =
      props?.theme && props?.theme?.disableStyledSystemCache;

    //we loop through the props
    for (const key in props) {
      //we check if the prop is a prop we want to map to css
      if (!config[key]) continue;

      //we get the function that will extract the css from the prop
      const extractCssFromProps = config[key];

      //we get the raw value of the prop
      const rawPropValue = props[key];
      //we get the scale from the theme object
      const scale = getNestedPropertyOrDefault(
        props.theme,
        extractCssFromProps.scale,
        extractCssFromProps.defaults,
      );

      //we check if the prop is a responsive prop
      if (typeof rawPropValue === 'object') {
        //we get the breakpoints from the theme object ( we use the default breakpoints if the breakpoints are not found in the theme object )
        cache.breakpoints =
          (!isCacheDisabled && cache.breakpoints) ||
          getNestedPropertyOrDefault(
            props.theme,
            'breakpoints',
            defaultBreakpoints,
          );

        if (rawPropValue !== null) {
          styles = merge(
            styles,
            parseResponsiveObject(
              cache.breakpoints,
              extractCssFromProps,
              scale,
              rawPropValue,
              props,
            ),
          );
          shouldSort = true;
        }
        continue;
      }

      Object.assign(styles, extractCssFromProps(rawPropValue, scale, props));
    }

    // sort object-based responsive styles
    if (shouldSort) {
      styles = sort(styles);
    }

    return styles;
  };
  parse.config = config;
  parse.propNames = Object.keys(config);
  parse.cache = cache;

  const keys = Object.keys(config).filter((k) => k !== 'config');
  if (keys.length > 1) {
    keys.forEach((key) => {
      (parse as any)[key] = createParser({ [key]: config[key] });
    });
  }

  return parse;
};
